import { useState, useSyncExternalStore } from "react";

import { useBootstrap } from "../api/queries/useBootstrap";
import {
  armFailNext,
  getArmedFault,
  getLastProblemInstance,
  subscribeDevTools,
  FAULT_STATUSES,
  type FaultStatus,
} from "../api/devTools";
import { DEV_TOOLS } from "../api/devFlag";
import { useSession } from "../api/session";
import { Button } from "../components/ui/button";
import { useUiStore, type PlatformOverride } from "../store/uiStore";
import { modules } from "../tenants";
import { usePlatform, usePlatformSource } from "./usePlatform";

const OVERRIDES: PlatformOverride[] = ["auto", "desktop", "tablet", "phone"];

function useDevToolsState() {
  return useSyncExternalStore(
    subscribeDevTools,
    () => `${getArmedFault() ?? "-"}|${getLastProblemInstance() ?? "-"}`,
    () => "-|-",
  );
}

/**
 * The dev footer. It and `devTools.ts`'s wrapper sit behind the ONE build-time flag and compile
 * out together. It shows who is signed in but cannot change it: sign-in is the real one with or
 * without the flag (AU-D14).
 */
export function DevToolsFooter() {
  const { data } = useBootstrap();
  const platform = usePlatform();
  const source = usePlatformSource();
  const { account } = useSession();
  const platformOverride = useUiStore((s) => s.platformOverride);
  const setPlatformOverride = useUiStore((s) => s.setPlatformOverride);
  const hiddenModules = useUiStore((s) => s.hiddenModules);
  const setHiddenModules = useUiStore((s) => s.setHiddenModules);
  const [status, setStatus] = useState<FaultStatus>(500);
  const [open, setOpen] = useState(false);
  const devState = useDevToolsState();
  const [armed, lastInstance] = devState.split("|");

  if (!DEV_TOOLS) return null;

  const toggleModule = (key: string) => {
    setHiddenModules(
      hiddenModules.includes(key)
        ? hiddenModules.filter((k) => k !== key)
        : [...hiddenModules, key],
    );
  };

  return (
    <div className="sticky bottom-0 z-20 border-t border-border bg-surface-sunken">
      {/* The state line — always visible. Half the acceptance checks read "as {account}, at
          {platform}", and stating both removes the commonest false failure. */}
      <div className="flex flex-wrap items-center gap-sm px-md py-xs">
        <span className="type-caption text-muted-foreground">DEV</span>
        <span className="type-key text-foreground">
          {account?.email ?? account?.name ?? "anonymous"}
        </span>
        <span className="type-caption text-muted-foreground">
          {platform} ({source})
        </span>
        <span className="type-key text-foreground">{data?.tenant.key ?? "—"}</span>
        <span className="type-caption text-muted-foreground">
          {data?.tenant.environment ?? "—"}
        </span>
        <span className="type-caption text-muted-foreground">
          last problem: <span className="type-key">{lastInstance || "—"}</span>
        </span>
        {armed && armed !== "-" ? (
          <span className="type-caption text-state-upload-invalid-fg">armed: {armed}</span>
        ) : null}
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)}>
          {open ? "Hide tools" : "Dev tools"}
        </Button>
      </div>

      {open ? (
        <div className="flex flex-wrap items-start gap-lg border-t border-border px-md py-sm">
          <section>
            <h2 className="type-caption text-muted-foreground">Platform</h2>
            <div className="mt-xs flex gap-xs">
              {OVERRIDES.map((o) => (
                <Button
                  key={o}
                  size="sm"
                  variant={platformOverride === o ? "default" : "outline"}
                  onClick={() => setPlatformOverride(o)}
                >
                  {o}
                </Button>
              ))}
            </div>
          </section>

          <section>
            <h2 className="type-caption text-muted-foreground">Fail next request</h2>
            <div className="mt-xs flex items-center gap-xs">
              <select
                aria-label="Fault status"
                className="type-body h-control-sm rounded-md border border-input bg-background px-xs text-foreground"
                value={status}
                onChange={(e) => setStatus(Number(e.target.value) as FaultStatus)}
              >
                {FAULT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={() => armFailNext(status)}>
                Arm
              </Button>
            </div>
          </section>

          <section>
            <h2 className="type-caption text-muted-foreground">Hide module</h2>
            <div className="mt-xs flex flex-col gap-xs">
              {modules.map((m) => (
                <label key={m.key} className="type-body flex items-center gap-xs text-foreground">
                  <input
                    type="checkbox"
                    checked={hiddenModules.includes(m.key)}
                    onChange={() => toggleModule(m.key)}
                  />
                  {m.key}
                </label>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
